export const GA4_MEASUREMENT_ID = "G-PYJRCBPZ57";
export const CLARITY_PROJECT_ID = "yteqz5ojro";
export const ANALYTICS_CONSENT_KEY = "apping-analytics-consent";
const PRODUCTION_HOST = "appingtechnology.com";
const EVENTS = new Set([
  "book_walkthrough_click",
  "walkthrough_form_start",
  "walkthrough_form_submit",
  "walkthrough_form_success",
]);

export function createAnalyticsController(win, doc) {
  let initialized = false;
  let trackedPath = null;

  function getConsent() {
    try {
      const value = win.localStorage.getItem(ANALYTICS_CONSENT_KEY);
      return value === "accepted" || value === "rejected" ? value : null;
    } catch {
      return null;
    }
  }

  function saveConsent(value) {
    if (value !== "accepted" && value !== "rejected") return;
    try {
      win.localStorage.setItem(ANALYTICS_CONSENT_KEY, value);
    } catch {
      // A storage restriction must not enable tracking without consent.
    }
    if (value === "accepted") initialize();
    else if (initialized) {
      win.gtag?.("consent", "update", { analytics_storage: "denied", ad_storage: "denied" });
      win.clarity?.("consentv2", { analytics_Storage: "denied", ad_Storage: "denied" });
      // Unload third-party scripts and end this session when consent is withdrawn.
      win.location.reload();
    }
  }

  function trackEvent(name) {
    if (!initialized || !EVENTS.has(name) || getConsent() !== "accepted") return;
    // Do not pass form values, URLs with query strings, or other visitor data.
    win.gtag("event", name);
  }

  function handleClick(event) {
    const anchor = event.target?.closest?.("a[href]");
    if (!anchor) return;
    try {
      const destination = new URL(anchor.href, win.location.origin);
      if (destination.origin === win.location.origin && destination.pathname.replace(/\/$/, "") === "/book-a-walkthrough") {
        trackEvent("book_walkthrough_click");
      }
    } catch {
      // Ignore malformed links.
    }
  }

  function initialize() {
    if (initialized || win.location.hostname !== PRODUCTION_HOST || getConsent() !== "accepted") return false;
    initialized = true;
    const pathname = win.location.pathname || "/";
    const pageLocation = `https://${PRODUCTION_HOST}${pathname}`;
    let referrer = "";
    try {
      const source = new URL(doc.referrer);
      referrer = source.origin;
    } catch {
      // A missing or malformed referrer is not used for analytics.
    }

    win.dataLayer = win.dataLayer || [];
    win.gtag = win.gtag || function (...args) { win.dataLayer.push(args); };
    win.gtag("consent", "default", { analytics_storage: "granted", ad_storage: "denied" });
    win.gtag("js", new Date());
    win.gtag("config", GA4_MEASUREMENT_ID, {
      send_page_view: false,
      page_location: pageLocation,
      page_referrer: referrer,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    win.clarity = win.clarity || function (...args) {
      (win.clarity.q = win.clarity.q || []).push(args);
    };
    win.clarity("consentv2", { analytics_Storage: "granted", ad_Storage: "denied" });

    const gaScript = doc.createElement("script");
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    doc.head.appendChild(gaScript);

    const clarityScript = doc.createElement("script");
    clarityScript.async = true;
    clarityScript.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
    doc.head.appendChild(clarityScript);

    if (trackedPath !== pathname) {
      trackedPath = pathname;
      win.gtag("event", "page_view", {
        page_location: pageLocation,
        page_referrer: referrer,
        page_path: pathname,
        page_title: doc.title,
        send_to: GA4_MEASUREMENT_ID,
      });
    }
    doc.addEventListener("click", handleClick, true);
    return true;
  }

  return { getConsent, saveConsent, initialize, trackEvent };
}

let browserController;
export function analytics() {
  if (!browserController) browserController = createAnalyticsController(window, document);
  return browserController;
}
